'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useContext, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext } from '@/entities/cart';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { QueryApi } from '@/features/query/api';
import { Button } from '@/shared/ui/button';
import { FormAddressAutocompleteField } from '@/shared/ui/form/form-address-autocomplete-field';
import { FormUserAddressSelect } from '@/shared/ui/form/form-user-address-select';
import { Tabs } from '@/shared/ui/tabs';
import { SelectVenueModalContext } from './SelectVenueModalProvider';
import './styles.scss';

export const SelectVenueModalSearchFields = () => {
    const t = useTranslations();

    const { formMethods: cartFormMethods } = useContext(CartContext);
    const {
        formMethods,
        handleChangeDeliveryType,
        handleChangeAddress,
        areSearchFieldsVisible,
        isDeliveryAvailable,
        isPickupAvailable,
    } = useContext(SelectVenueModalContext);

    const [deliveryType, isManual] = useWatch({
        control: formMethods.control,
        name: ['deliveryType', 'isManual'],
    });

    const { data: isLoggedIn } = useQuery({
        ...QueryApi.Customer.queries.Customer,
        select: (data) => !!data,
    });
    const { data: allAddresses = [] } = useQuery({
        ...QueryApi.Customer.queries.Addresses({ offset: 0, limit: 100 }),
        enabled: isLoggedIn,
        select: (data) => data.items,
    });

    const handleSelectFromSaved = () => {
        formMethods.setValue('isManual', false);
        const defaultAddress = allAddresses.find((a) => a.isDefault) ?? allAddresses[0];
        if (defaultAddress) {
            const newAddress = { id: defaultAddress.id, ...defaultAddress.address };
            handleChangeAddress(newAddress);
            formMethods.setValue('address', newAddress);
        }
    };

    const handleEnterManually = () => {
        formMethods.setValue('isManual', true);
    };

    // Sync form address with cart when changed (non-invalid)
    useEffect(() => {
        const unsubscribe = formMethods.subscribe({
            name: 'address',
            formState: { values: true },
            callback: (data) => {
                if (!data.values.address) {
                    return;
                }
                const opts = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
                cartFormMethods.setValue('deliveryMethod.address', data.values.address, opts);
                cartFormMethods.setValue('deliveryMethod.addressId', data.values.address.id ?? null, opts);
                cartFormMethods.setValue('deliveryMethod.manual', data.values.isManual, opts);
            },
        });
        return unsubscribe;
    }, []);

    // After login, if no address is confirmed, switch to saved mode and select default
    useEffect(() => {
        if (isLoggedIn && !cartFormMethods.getValues('deliveryMethod.address') && allAddresses.length > 0) {
            handleSelectFromSaved();
        }
    }, [isLoggedIn, allAddresses]);

    if (!areSearchFieldsVisible) {
        return null;
    }

    return (
        <div className="select-venue-modal__search-fields">
            <Tabs value={deliveryType} onValueChange={handleChangeDeliveryType}>
                <Tabs.List>
                    {isDeliveryAvailable && (
                        <Tabs.Trigger value={DeliveryType.DELIVERY}>{t('WORDS.DELIVERY')}</Tabs.Trigger>
                    )}
                    {isPickupAvailable && (
                        <Tabs.Trigger value={DeliveryType.PICKUP}>{t('WORDS.PICKUP')}</Tabs.Trigger>
                    )}
                </Tabs.List>
            </Tabs>
            {deliveryType === DeliveryType.DELIVERY && (
                <>
                    {!isLoggedIn || isManual ? (
                        <FormAddressAutocompleteField
                            name="address"
                            control={formMethods.control}
                            onChange={handleChangeAddress}
                            additionalButtons={
                                isLoggedIn ? (
                                    <Button
                                        variant="outlined"
                                        className="address-select__button"
                                        data-testid="select-venue-modal-select-from-saved-button"
                                        onClick={handleSelectFromSaved}
                                    >
                                        {t('SELECT_VENUE_MODAL.SELECT_FROM_SAVED')}
                                    </Button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <FormUserAddressSelect
                            name="address"
                            control={formMethods.control}
                            onValueChange={(_, address) => handleChangeAddress(address)}
                            triggerProps={{ 'data-testid': 'select-venue-modal-address-select' }}
                            additionalButtons={
                                <Button
                                    variant="outlined"
                                    className="address-select__button"
                                    data-testid="select-venue-modal-enter-manually-button"
                                    onClick={handleEnterManually}
                                >
                                    {t('SELECT_VENUE_MODAL.ENTER_MANUALLY')}
                                </Button>
                            }
                        />
                    )}
                </>
            )}
        </div>
    );
};
